/**
 * useCursorStatementEditInfo — classify the statement under the cursor for the
 * minimal edit affordance. Returns the Resolver `statementId`, the `RenderMode`,
 * and the derived affordance target (`'compact' | 'ddl-card' | null` —
 * SELECT/DML map to `'compact'`; the 7 true-DDL `StatementKind`s map to
 * `'ddl-card'` (the compact DDL summary card); everything else maps to
 * `null`) so the gutter / inline action components can decide whether to show
 * the pencil and the Compact VQE popover knows which statement to wire.
 *
 * The `'ddl-card'` target is gated on the classified statement's coarse
 * `StatementKind`, not `RenderMode` alone — see `vqeEditAffordance.ts`'s
 * module docstring for why (`detectRenderMode`'s catch-all `default:` arm
 * folds EXPLAIN/SET/etc. into the same `'unsupported-ddl'` mode as true DDL).
 *
 * Mirrors the parse the old inline toolbar ran (`classifyAtCursor`) but is a
 * pure derive over `(query, statementRange)` — no Monaco subscription. The
 * editor host already tracks `currentCursorStatement`; we re-classify off its
 * range so the affordance and the host stay in sync without a second cursor
 * listener.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS2;
 * Plan 9 §2.9/S13 for the `'ddl-card'` target.
 */

import { useMemo } from 'react';

import type { ParseOptions } from '../../../sql-grammar/model';
import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import { classifyAtCursor, lineColumnToOffset } from '../inline/useInlineToolbarPosition';
import type { RenderMode } from '../types';
import {
  resolveEditAffordanceTarget,
  resolveUnavailableReason,
  type VqeEditAffordanceTarget,
  type VqeUnavailableReason,
} from './vqeEditAffordance';

/** Where `unavailableReason` came from — which parse signal produced it.
 * `null` when there's no reason (editable, or nothing to teach).
 * `'render-mode'` is the pre-existing signal: `resolveUnavailableReason`
 * mapped the LOCAL (lenient) grammar's `RenderMode` (procedural / mixed
 * set-op / the rare cases where even the lenient grammar gives up).
 *
 * The other two are the EDG-11..15 parse-error signals — both override an
 * otherwise-editable statement, and both differ only in whether a SECOND
 * squiggle would double up:
 *   • `'lsp-marker'` — a real Error marker under owner `'sql'` (PostgreSQL LSP
 *     / mock) or `'sql-syntax'` (MSSQL ANTLR∧tree-sitter) already overlaps the
 *     span AND draws its own squiggle, so `useParseErrorMarker`'s dedicated
 *     `vqe-parse` marker MUST be suppressed (no double squiggle).
 *   • `'local-parse'` — the caller's LOCAL tree-sitter predicate
 *     (`localSyntaxErrorSignal.ts`) fired and NO external marker covers the
 *     span, so `vqe-parse` is the authoritative squiggle and MUST paint.
 * Callers key the squiggle off exactly this distinction (see
 * `QueryEditorTabContent.tsx`). */
export type UnavailableReasonSource = 'render-mode' | 'lsp-marker' | 'local-parse' | null;

export type CursorStatementEditInfo = {
  readonly statementId: string | null;
  readonly mode: RenderMode | null;
  readonly target: VqeEditAffordanceTarget;
  /**
   * Why the statement can't be edited visually, when `target` is `null` — the
   * teaching signal behind the never-silent affordance (procedural / mixed
   * set-op / genuine parse-error). `null` when the statement IS editable (a
   * pencil shows) or when there's genuinely nothing to teach (empty / comment /
   * whitespace / out-of-scope kind). Never non-null alongside a non-null
   * `target`.
   */
  readonly unavailableReason: VqeUnavailableReason;
  /** See {@link UnavailableReasonSource}. */
  readonly unavailableReasonSource: UnavailableReasonSource;
};

const EMPTY: CursorStatementEditInfo = {
  statementId: null,
  mode: null,
  target: null,
  unavailableReason: null,
  unavailableReasonSource: null,
};

export function useCursorStatementEditInfo(
  query: string,
  statementRange: EditorSelectionRange | null,
  options: ParseOptions,
  /**
   * True when a real Error-severity marker under owner `'sql'` (PostgreSQL LSP
   * / mock) or `'sql-syntax'` (MSSQL ANTLR∧tree-sitter) overlaps the cursor
   * statement — computed by the caller via `lspParseErrorSignal.ts`'s
   * `hasOverlappingSyntaxError`. When this is the source of the override, the
   * external marker already draws the squiggle (source `'lsp-marker'`).
   * Defaults to `false` (BUG-4 non-regression: a statement with no signal
   * classifies exactly as before).
   */
  hasMarkerSyntaxError = false,
  /**
   * True when the caller's LOCAL tree-sitter predicate
   * (`localSyntaxErrorSignal.ts`) confirmed the cursor statement is broken and
   * NO external marker covers it (EDG-11..15, the four engines with no
   * language-service marker). When this is the source of the override, the
   * dedicated `vqe-parse` squiggle is authoritative (source `'local-parse'`).
   * Defaults to `false`.
   */
  hasLocalSyntaxError = false,
): CursorStatementEditInfo {
  return useMemo<CursorStatementEditInfo>(() => {
    if (!statementRange || query.trim().length === 0) return EMPTY;
    const cursorOffset = lineColumnToOffset(
      query,
      statementRange.startLineNumber,
      statementRange.startColumn,
    );
    const classified = classifyAtCursor({ query, cursorOffset, options });
    if (!classified || !classified.statement) return EMPTY;
    const target = resolveEditAffordanceTarget(classified.renderMode, classified.statement.kind);

    // Parse-error override (EDG-11..15): the lenient local grammar considers
    // this statement editable, but a real syntax-error signal overlaps it — the
    // popover would otherwise try to render a structured editor over garbage.
    // Hide the pencil and surface the same `'parse-error'` teaching reason
    // lane-h wired downstream. Only fires when the statement WOULD be editable
    // (`target` non-null) — a statement already unavailable for another reason
    // (procedural / set-op-precedence) keeps its own honest reason, untouched.
    // The source records which signal fired so the caller can avoid a double
    // squiggle: a `'lsp-marker'` already has its own squiggle; a `'local-parse'`
    // needs `useParseErrorMarker`'s `vqe-parse` marker. Marker wins when both
    // fire (the external squiggle is already on-screen).
    if (target !== null && (hasMarkerSyntaxError || hasLocalSyntaxError)) {
      return {
        statementId: classified.statement.id,
        mode: classified.renderMode,
        target: null,
        unavailableReason: 'parse-error',
        unavailableReasonSource: hasMarkerSyntaxError ? 'lsp-marker' : 'local-parse',
      };
    }

    // Only surface a reason when there's no editable target — a defensive
    // pairing so a reason never rides alongside a visible pencil even if the
    // mode→reason map ever drifts.
    const unavailableReason =
      target === null ? resolveUnavailableReason(classified.renderMode) : null;
    return {
      statementId: classified.statement.id,
      mode: classified.renderMode,
      target,
      unavailableReason,
      unavailableReasonSource: unavailableReason ? 'render-mode' : null,
    };
  }, [query, statementRange, options, hasMarkerSyntaxError, hasLocalSyntaxError]);
}
